import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, History, RotateCcw, Loader2, Eye, EyeOff, Tag, Check, Diff } from "lucide-react";

/**
 * Right-side timeline of report versions.
 * - Lists every checkpointed version (newest first)
 * - Click a row to preview that snapshot in the main canvas
 * - Inline label input (Tag icon) per version — pin & name key milestones
 * - Restore button auto-checkpoints the current draft first
 * - Diff button flips the preview panel between "Preview" and "Diff vs live"
 */

/* ---------- diff helpers ---------- */
function shallow(a, b) { return JSON.stringify(a ?? null) === JSON.stringify(b ?? null); }

function computeDiff(oldDoc, newDoc) {
  // Returns { name, meta:[{k, from, to}], elements:{added, removed, modified:[{id,type,changes}]} }
  const out = { meta: [], elements: { added: [], removed: [], modified: [] }, name: null };
  if ((oldDoc?.name || "") !== (newDoc?.name || "")) {
    out.name = { from: oldDoc?.name || "", to: newDoc?.name || "" };
  }
  for (const k of ["page", "header", "footer", "watermark", "theme", "parameters"]) {
    if (!shallow(oldDoc?.[k], newDoc?.[k])) out.meta.push({ k, from: oldDoc?.[k] ?? null, to: newDoc?.[k] ?? null });
  }
  const oldEls = oldDoc?.elements || [];
  const newEls = newDoc?.elements || [];
  const oldById = new Map(oldEls.map((e) => [e.id, e]));
  const newById = new Map(newEls.map((e) => [e.id, e]));
  for (const e of newEls) if (!oldById.has(e.id)) out.elements.added.push(e);
  for (const e of oldEls) if (!newById.has(e.id)) out.elements.removed.push(e);
  for (const e of newEls) {
    const prior = oldById.get(e.id);
    if (!prior) continue;
    if (!shallow(prior, e)) {
      const changes = [];
      if (prior.type !== e.type) changes.push(`type: ${prior.type} → ${e.type}`);
      if (!shallow(prior.config, e.config)) changes.push("config updated");
      out.elements.modified.push({ id: e.id, type: e.type, changes });
    }
  }
  return out;
}

function DiffBlock({ diff }) {
  const totalMeta = (diff.name ? 1 : 0) + diff.meta.length;
  const totalEl = diff.elements.added.length + diff.elements.removed.length + diff.elements.modified.length;
  if (!totalMeta && !totalEl) {
    return <div data-testid="rd-version-diff-empty" className="text-[11px] text-[color:var(--ink-3)] italic px-2 py-3">No differences — this version is identical to the current draft.</div>;
  }
  return (
    <div data-testid="rd-version-diff" className="space-y-2">
      {diff.name && (
        <div className="text-[10px] rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)] px-2 py-1.5">
          <div className="font-semibold text-[color:var(--ink-2)]">Report name</div>
          <div className="text-[color:var(--danger)] line-through truncate">- {diff.name.from || "(empty)"}</div>
          <div className="text-[color:var(--success)] truncate">+ {diff.name.to || "(empty)"}</div>
        </div>
      )}
      {diff.meta.length > 0 && (
        <div className="text-[10px] rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)] px-2 py-1.5">
          <div className="font-semibold text-[color:var(--ink-2)] mb-1">Report settings</div>
          {diff.meta.map((m) => <div key={m.k} className="truncate text-[color:var(--ink-2)]">· {m.k} changed</div>)}
        </div>
      )}
      {(diff.elements.added.length + diff.elements.removed.length + diff.elements.modified.length) > 0 && (
        <div className="text-[10px] rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)] px-2 py-1.5">
          <div className="font-semibold text-[color:var(--ink-2)] mb-1">Elements</div>
          {diff.elements.added.map((e) => (
            <div key={"a" + e.id} className="text-[color:var(--success)] truncate">+ added {e.type}</div>
          ))}
          {diff.elements.removed.map((e) => (
            <div key={"r" + e.id} className="text-[color:var(--danger)] truncate">- removed {e.type}</div>
          ))}
          {diff.elements.modified.map((e) => (
            <div key={"m" + e.id} className="text-[color:var(--ink-2)] truncate">~ modified {e.type} <span className="text-[color:var(--ink-3)]">({e.changes.join(", ") || "changed"})</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- inline label editor ---------- */
function LabelEditor({ initialLabel, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initialLabel || "");
  useEffect(() => setVal(initialLabel || ""), [initialLabel]);
  const submit = async (e) => {
    e?.stopPropagation();
    await onSave(val.trim());
    setEditing(false);
  };
  if (!editing) {
    return (
      <button data-testid={testId} onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-[color:var(--glass-border)] hover:border-[color:var(--brand-ocean)] hover:text-[color:var(--brand-ocean)] ${initialLabel ? "bg-[color:var(--brand-mint)]/15 text-[color:var(--brand-ocean)] font-medium" : "bg-[color:var(--surface)] text-[color:var(--ink-3)]"}`}>
        <Tag className="w-3 h-3" />{initialLabel || "Label"}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
      <Input data-testid={`${testId}-input`} value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(e); if (e.key === "Escape") setEditing(false); }}
        autoFocus placeholder="e.g. Q4 board pack" maxLength={80}
        className="h-6 text-[11px] bg-[color:var(--surface)]" />
      <button data-testid={`${testId}-save`} onClick={submit}
        className="p-1 rounded hover:bg-[color:var(--brand-ocean)]/10 text-[color:var(--brand-ocean)]">
        <Check className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ---------- main pane ---------- */
export function VersionTimeline({ open, onOpenChange, reportId, liveDraft, onPreviewChange, onCompareChange, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [compareWith, setCompareWith] = useState(null);   // secondary version {index, data} for two-up compare
  const [mode, setMode] = useState("preview");            // "preview" | "diff"
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const loadVersions = React.useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/report-designs/${reportId}/versions`);
      setVersions((data || []).slice().reverse());
    } catch { setVersions([]); }
    setLoading(false);
  }, [reportId]);

  useEffect(() => { if (open) loadVersions(); }, [open, loadVersions]);
  useEffect(() => {
    if (!open) { setSelected(null); setCompareWith(null); onPreviewChange?.(null); onCompareChange?.(null); setMode("preview"); }
  }, [open, onPreviewChange, onCompareChange]);

  const showVersion = async (index) => {
    if (selected?.index === index) {
      setSelected(null);
      setCompareWith(null);
      onPreviewChange?.(null);
      onCompareChange?.(null);
      return;
    }
    try {
      const { data } = await api.get(`/report-designs/${reportId}/versions/${index}`);
      setSelected({ index, data: data.data || {}, meta: { saved_at: data.saved_at, saved_by: data.saved_by, label: data.label } });
      setCompareWith(null);
      onCompareChange?.(null);
      onPreviewChange?.(data.data || {});
    } catch { toast.error("Could not load that version"); }
  };

  const pickCompareWith = async (indexStr) => {
    if (indexStr === "" || indexStr == null) { setCompareWith(null); onCompareChange?.(null); return; }
    const idx = parseInt(indexStr);
    try {
      const { data } = await api.get(`/report-designs/${reportId}/versions/${idx}`);
      const cmp = { index: idx, data: data.data || {}, label: data.label };
      setCompareWith(cmp);
      onCompareChange?.(cmp);
      setMode("diff");
    } catch { toast.error("Could not load compare version"); }
  };

  const restore = async () => {
    if (selected == null) return;
    setRestoring(true);
    try {
      await api.post(`/report-designs/${reportId}/versions/${selected.index}/restore`);
      toast.success("Version restored");
      setSelected(null);
      setCompareWith(null);
      onPreviewChange?.(null);
      onCompareChange?.(null);
      onRestored?.();
      onOpenChange(false);
    } catch { toast.error("Restore failed"); }
    setRestoring(false);
  };

  const saveLabel = async (index, label) => {
    try {
      await api.patch(`/report-designs/${reportId}/versions/${index}`, { label });
      toast.success(label ? "Label saved" : "Label cleared");
      loadVersions();
    } catch { toast.error("Failed to save label"); }
  };

  // Diff target: compareWith (if picked) else the live draft
  const diffAgainst = compareWith?.data || liveDraft;
  const diffLabel = compareWith
    ? (compareWith.label || `Version ${compareWith.index + 1}`)
    : "Live draft";
  const diff = useMemo(() => (selected && diffAgainst) ? computeDiff(selected.data, diffAgainst) : null, [selected, diffAgainst]);

  if (!open) return null;

  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 30) return `${days}d ago`;
      return d.toLocaleDateString();
    } catch { return iso; }
  };

  return (
    <aside data-testid="rd-version-timeline" className="w-[320px] shrink-0 border-l border-[color:var(--glass-border)] bg-[color:var(--surface)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--glass-border)]">
        <div className="flex items-center gap-1.5">
          <History className="w-4 h-4 text-[color:var(--brand-ocean)]" />
          <span className="font-display text-sm text-[color:var(--ink)]">Version history</span>
        </div>
        <button data-testid="rd-version-close" onClick={() => onOpenChange(false)}
          className="p-1 rounded hover:bg-[color:var(--surface-2)] text-[color:var(--ink-3)]">
          <X className="w-4 h-4" />
        </button>
      </div>

      {selected != null && (
        <div className="mx-3 mt-2 space-y-1.5">
          <div data-testid="rd-version-preview-banner" className="p-2 rounded-lg bg-[color:var(--brand-ocean)]/10 border border-[color:var(--brand-ocean)]/40 text-[11px] text-[color:var(--brand-ocean)] flex items-center gap-2">
            <Eye className="w-3.5 h-3.5" />
            <span className="flex-1 truncate">
              {mode === "diff" ? `Diff · v${selected.index + 1} vs ${diffLabel}` : `Previewing v${selected.index + 1}`}
              {selected.meta?.label && mode !== "diff" && <span className="ml-1 font-semibold">· {selected.meta.label}</span>}
            </span>
            <button data-testid="rd-version-preview-clear" onClick={() => { setSelected(null); setCompareWith(null); onPreviewChange?.(null); onCompareChange?.(null); setMode("preview"); }}
              className="text-[10px] font-medium hover:underline">Live draft</button>
          </div>
          {/* Mode toggle: preview vs diff */}
          <div className="flex text-[10px] rounded-lg border border-[color:var(--glass-border)] overflow-hidden">
            <button data-testid="rd-version-mode-preview" onClick={() => setMode("preview")}
              className={`flex-1 px-2 py-1 flex items-center justify-center gap-1 ${mode === "preview" ? "bg-[color:var(--brand-ocean)] text-white" : "bg-[color:var(--surface)] text-[color:var(--ink-2)]"}`}>
              <Eye className="w-3 h-3" />Preview
            </button>
            <button data-testid="rd-version-mode-diff" onClick={() => setMode("diff")}
              className={`flex-1 px-2 py-1 flex items-center justify-center gap-1 ${mode === "diff" ? "bg-[color:var(--brand-ocean)] text-white" : "bg-[color:var(--surface)] text-[color:var(--ink-2)]"}`}>
              <Diff className="w-3 h-3" />Diff vs {compareWith ? `v${compareWith.index + 1}` : "live"}
            </button>
          </div>
          {/* Compare-with picker (two-up mode). Only offered when there are ≥2 versions. */}
          {versions.length >= 2 && (
            <div className="flex items-center gap-1.5 text-[10px]">
              <span className="text-[color:var(--ink-3)] shrink-0">Compare with:</span>
              <select data-testid="rd-version-compare-with" value={compareWith?.index ?? ""}
                onChange={(e) => pickCompareWith(e.target.value)}
                className="flex-1 h-6 rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-1 text-[10px]">
                <option value="">Live draft</option>
                {versions.filter((v) => v.index !== selected.index).map((v) => (
                  <option key={v.index} value={v.index}>
                    v{v.index + 1}{v.label ? ` · ${v.label}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {selected != null && mode === "diff" && diff && <DiffBlock diff={diff} />}
        {(selected == null || mode !== "diff") && (
          <>
            {loading && (
              <div className="text-center py-8 text-[color:var(--ink-3)] text-xs">
                <Loader2 className="w-4 h-4 mx-auto mb-1 animate-spin" />Loading versions…
              </div>
            )}
            {!loading && versions.length === 0 && (
              <div className="text-[11px] text-[color:var(--ink-3)] text-center py-6">
                No versions yet — checkpoints are captured automatically every 10 minutes as you save.
              </div>
            )}
            {!loading && versions.map((v) => {
              const isSel = selected?.index === v.index;
              return (
                <div key={v.index} data-testid={`rd-version-row-${v.index}`}
                  className={`rounded-lg border p-2 cursor-pointer transition-colors
                    ${isSel ? "border-[color:var(--brand-ocean)] bg-[color:var(--brand-ocean)]/5"
                            : "border-[color:var(--glass-border)] hover:bg-[color:var(--surface-2)]"}`}
                  onClick={() => showVersion(v.index)}>
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col items-center pt-1">
                      <div className={`w-2 h-2 rounded-full ${isSel ? "bg-[color:var(--brand-ocean)]" : "bg-[color:var(--ink-3)]/40"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-[color:var(--ink)] truncate">
                        {v.label ? <span data-testid={`rd-version-label-badge-${v.index}`} className="text-[color:var(--brand-ocean)]">{v.label}</span> : (v.name || "Untitled")}
                        <span className="ml-1.5 text-[10px] font-normal text-[color:var(--ink-3)]">#{v.index + 1}</span>
                      </div>
                      <div className="text-[10px] text-[color:var(--ink-3)] mt-0.5">
                        {fmtWhen(v.saved_at)} · by {v.saved_by || "unknown"}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <button data-testid={`rd-version-preview-${v.index}`}
                          onClick={(e) => { e.stopPropagation(); showVersion(v.index); }}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] hover:border-[color:var(--brand-ocean)] hover:text-[color:var(--brand-ocean)]">
                          {isSel ? <><EyeOff className="w-3 h-3" />Hide</> : <><Eye className="w-3 h-3" />Preview</>}
                        </button>
                        <LabelEditor initialLabel={v.label} onSave={(lbl) => saveLabel(v.index, lbl)}
                          testId={`rd-version-label-${v.index}`} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {selected != null && (
        <div className="border-t border-[color:var(--glass-border)] p-2">
          <Button data-testid={`rd-version-restore-${selected.index}`}
            onClick={restore} disabled={restoring}
            className="w-full h-9 text-xs bg-[color:var(--brand-ocean)] text-white">
            {restoring ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
            Restore this version
          </Button>
          <p className="text-[9.5px] text-[color:var(--ink-3)] mt-1.5 text-center">
            The current draft will be checkpointed automatically before restore.
          </p>
        </div>
      )}
    </aside>
  );
}
